/* eslint-disable @atlassian/i18n/enforce-id-by-package -- Preserve existing translation IDs when moving the card from Rovo UI to smart-knowledge-cards. */
import { defineMessages, type MessageDescriptor } from 'react-intl-next';

const messages: Record<
	| 'inferredProject'
	| 'inferredProjectTrigger'
	| 'loading'
	| 'loadError'
	| 'basedOnPrefix'
	| 'teamworkGraphActivityAndSource'
	| 'sourcedFrom'
	| 'suggestedContributors'
	| 'suggestedContributorsWithCount'
	| 'noSuggestedContributors'
	| 'showAllContributors'
	| 'claimProjectDescription'
	| 'claimProject'
	| 'claimProjectError'
	| 'viewProject'
	| 'copyLink'
	| 'linkCopied'
	| 'project'
	| 'projectTrigger'
	| 'sourcesAndRelatedLinks'
	| 'owner'
	| 'contributors'
	| 'noContributors'
	| 'projectConfirmed'
	| 'relatedContent'
	| 'aiDisclaimer'
	| 'goodSuggestion'
	| 'poorSuggestion'
	| 'feedbackThankYou'
	| 'dismissFeedbackConfirmation'
	| 'feedbackReasonsHeading'
	| 'closeFeedbackReasons'
	| 'additionalFeedbackLabel'
	| 'additionalFeedbackPlaceholder'
	| 'submitFeedbackReasons'
	| 'reasonNotAProject'
	| 'reasonWrongTitleOrDescription'
	| 'reasonUnrelatedSources'
	| 'reasonMissingSources'
	| 'reasonWrongContributors'
	| 'reasonAlreadyExists'
	| 'reasonOther',
	MessageDescriptor
> = defineMessages({
	inferredProject: {
		defaultMessage: 'Suggested project',
		description: 'Label shown on the inferred project card and inline project chip',
		id: 'rovo-platform-ui-components.inferred-project-card.inferredProjectCardMessages.inferredProject',
	},
	inferredProjectTrigger: {
		defaultMessage: '{projectName}: Suggested project',
		description: 'Accessible label for the suggested project chip trigger',
		id: 'rovo-platform-ui-components.inferred-project-card.inferredProjectCardMessages.inferredProjectTrigger',
	},
	loading: {
		defaultMessage: 'Loading project details',
		description: 'Status shown while inferred project details load',
		id: 'rovo-platform-ui-components.inferred-project-card.inferredProjectCardMessages.loading',
	},
	loadError: {
		defaultMessage: 'Project details could not be loaded.',
		description: 'Error shown when inferred project details fail to load',
		id: 'rovo-platform-ui-components.inferred-project-card.inferredProjectCardMessages.loadError',
	},
	basedOnPrefix: {
		defaultMessage: 'based on',
		description: 'Prefix for the inferred project source attribution',
		id: 'rovo-platform-ui-components.inferred-project-card.inferredProjectCardMessages.basedOnPrefix',
	},
	teamworkGraphActivityAndSource: {
		defaultMessage: 'Teamwork Graph activity and this source',
		description: 'Source attribution for an inferred project based on Teamwork Graph activity',
		id: 'rovo-platform-ui-components.inferred-project-card.inferredProjectCardMessages.teamworkGraphActivityAndSource',
	},
	sourcedFrom: {
		defaultMessage: 'sourced from',
		description: 'Sourced from text for an inferred project based on Teamwork Graph activity',
		id: 'rovo-platform-ui-components.inferred-project-card.inferredProjectCardMessages.sourcedFrom',
	},
	suggestedContributors: {
		defaultMessage: 'Suggested contributors',
		description: 'Heading for the inferred project contributors section',
		id: 'rovo-platform-ui-components.inferred-project-card.inferredProjectCardMessages.suggestedContributors',
	},
	suggestedContributorsWithCount: {
		defaultMessage: 'Suggested contributors ({count})',
		description: 'Heading for suggested contributors, with the total number of unique contributors',
		id: 'rovo-platform-ui-components.inferred-project-card.inferredProjectCardMessages.suggestedContributorsWithCount',
	},
	noSuggestedContributors: {
		defaultMessage: 'No suggested contributors',
		description: 'Empty state for the inferred project contributors section',
		id: 'rovo-platform-ui-components.inferred-project-card.inferredProjectCardMessages.noSuggestedContributors',
	},
	showAllContributors: {
		defaultMessage: 'Show all',
		description: 'Action to reveal all suggested contributors for an inferred project',
		id: 'rovo-platform-ui-components.inferred-project-card.inferredProjectCardMessages.showAllContributors',
	},
	claimProjectDescription: {
		defaultMessage: 'Claim this project so your team can track it together.',
		description: 'Supporting text above the action to promote an inferred project',
		id: 'rovo-platform-ui-components.inferred-project-card.inferredProjectCardMessages.claimProjectDescription',
	},
	claimProject: {
		defaultMessage: 'Claim this project',
		description: 'Action that promotes an inferred project to an Atlas project',
		id: 'rovo-platform-ui-components.inferred-project-card.inferredProjectCardMessages.claimProject',
	},
	claimProjectError: {
		defaultMessage: 'Error. Try again later.',
		description: 'Error shown when an inferred project cannot be promoted',
		id: 'rovo-platform-ui-components.inferred-project-card.inferredProjectCardMessages.claimProjectError',
	},
	viewProject: {
		defaultMessage: 'View project',
		description: 'Action that opens the promoted Atlas project',
		id: 'rovo-platform-ui-components.inferred-project-card.inferredProjectCardMessages.viewProject',
	},
	copyLink: {
		defaultMessage: 'Copy link',
		description: 'Action that copies the promoted Atlas project link',
		id: 'rovo-platform-ui-components.inferred-project-card.inferredProjectCardMessages.copyLink',
	},
	linkCopied: {
		defaultMessage: 'Link copied',
		description: 'Confirmation shown after the promoted Atlas project link is copied',
		id: 'rovo-platform-ui-components.inferred-project-card.inferredProjectCardMessages.linkCopied',
	},
	project: {
		defaultMessage: 'Project',
		description: 'Label shown after an inferred project has been promoted',
		id: 'rovo-platform-ui-components.inferred-project-card.inferredProjectCardMessages.project',
	},
	projectTrigger: {
		defaultMessage: '{projectName}: Project',
		description: 'Accessible label for the project chip after promotion',
		id: 'rovo-platform-ui-components.inferred-project-card.inferredProjectCardMessages.projectTrigger',
	},
	sourcesAndRelatedLinks: {
		defaultMessage: 'Sources and related links',
		description: 'Action that shows the source links for a promoted project',
		id: 'rovo-platform-ui-components.inferred-project-card.inferredProjectCardMessages.sourcesAndRelatedLinks',
	},
	owner: {
		defaultMessage: 'Owner',
		description: 'Heading for the promoted project owner',
		id: 'rovo-platform-ui-components.inferred-project-card.inferredProjectCardMessages.owner',
	},
	contributors: {
		defaultMessage: 'Contributors',
		description: 'Heading for the promoted project contributors',
		id: 'rovo-platform-ui-components.inferred-project-card.inferredProjectCardMessages.contributors',
	},
	noContributors: {
		defaultMessage: 'No contributors',
		description: 'Empty state for the promoted project contributors',
		id: 'rovo-platform-ui-components.inferred-project-card.inferredProjectCardMessages.noContributors',
	},
	projectConfirmed: {
		defaultMessage: 'Project confirmed',
		description: 'Confirmation shown after an inferred project is promoted',
		id: 'rovo-platform-ui-components.inferred-project-card.inferredProjectCardMessages.projectConfirmed',
	},
	relatedContent: {
		defaultMessage: 'Related content ({numRelated})',
		description:
			'Heading for the section listing documents, issues and pull requests related to the inferred project',
		id: 'rovo-platform-ui-components.inferred-project-card.inferredProjectCardMessages.relatedContent',
	},
	aiDisclaimer: {
		defaultMessage: 'Uses AI. Verify results.',
		description: 'Disclaimer in the inferred project card footer indicating AI-generated content',
		id: 'rovo-platform-ui-components.inferred-project-card.inferredProjectCardMessages.aiDisclaimer',
	},
	goodSuggestion: {
		defaultMessage: 'Good suggestion',
		description:
			'Accessible label and tooltip for giving positive feedback on a suggested project in its card',
		id: 'rovo-platform-ui-components.inferred-project-card.inferredProjectCardMessages.goodSuggestion',
	},
	poorSuggestion: {
		defaultMessage: 'Poor suggestion',
		description:
			'Accessible label and tooltip for giving negative feedback on a suggested project in its card',
		id: 'rovo-platform-ui-components.inferred-project-card.inferredProjectCardMessages.poorSuggestion',
	},
	feedbackThankYou: {
		defaultMessage: 'Thank you for your feedback!',
		description: 'Confirmation shown after the user gives feedback on a suggested project',
		id: 'rovo-platform-ui-components.inferred-project-card.inferredProjectCardMessages.feedbackThankYou',
	},
	dismissFeedbackConfirmation: {
		defaultMessage: 'Dismiss',
		description: 'Accessible label for the button that closes the feedback confirmation',
		id: 'rovo-platform-ui-components.inferred-project-card.inferredProjectCardMessages.dismissFeedbackConfirmation',
	},
	feedbackReasonsHeading: {
		defaultMessage: 'Tell us what should be changed',
		description:
			'Heading of the form that asks why a suggested project was rated poorly, shown after a thumbs-down',
		id: 'rovo-platform-ui-components.inferred-project-card.inferredProjectCardMessages.feedbackReasonsHeading',
	},
	closeFeedbackReasons: {
		defaultMessage: 'Close',
		description: 'Accessible label for the button that closes the suggested project feedback form',
		id: 'rovo-platform-ui-components.inferred-project-card.inferredProjectCardMessages.closeFeedbackReasons',
	},
	additionalFeedbackLabel: {
		defaultMessage: 'Additional feedback',
		description:
			'Label for the optional text field in the suggested project feedback form, shown after a thumbs-down',
		id: 'rovo-platform-ui-components.inferred-project-card.inferredProjectCardMessages.additionalFeedbackLabel',
	},
	additionalFeedbackPlaceholder: {
		defaultMessage: 'Tell us more',
		description:
			'Placeholder in the optional text field where the user explains what is wrong with a suggested project',
		id: 'rovo-platform-ui-components.inferred-project-card.inferredProjectCardMessages.additionalFeedbackPlaceholder',
	},
	submitFeedbackReasons: {
		defaultMessage: 'Submit feedback',
		description: 'Button that submits the selected reasons a suggested project was rated poorly',
		id: 'rovo-platform-ui-components.inferred-project-card.inferredProjectCardMessages.submitFeedbackReasons',
	},
	reasonNotAProject: {
		defaultMessage: 'Not a project',
		description: 'Feedback reason: the suggested project is not actually a project',
		id: 'rovo-platform-ui-components.inferred-project-card.inferredProjectCardMessages.reasonNotAProject',
	},
	reasonWrongTitleOrDescription: {
		defaultMessage: 'Wrong title or description',
		description: "Feedback reason: the suggested project's title or description is wrong",
		id: 'rovo-platform-ui-components.inferred-project-card.inferredProjectCardMessages.reasonWrongTitleOrDescription',
	},
	reasonUnrelatedSources: {
		defaultMessage: 'Unrelated sources',
		description: 'Feedback reason: the sources shown for the suggested project are unrelated to it',
		id: 'rovo-platform-ui-components.inferred-project-card.inferredProjectCardMessages.reasonUnrelatedSources',
	},
	reasonMissingSources: {
		defaultMessage: 'Missing sources',
		description: 'Feedback reason: the suggested project is missing relevant sources',
		id: 'rovo-platform-ui-components.inferred-project-card.inferredProjectCardMessages.reasonMissingSources',
	},
	reasonWrongContributors: {
		defaultMessage: 'Wrong contributors',
		description: 'Feedback reason: the suggested contributors for the project are wrong',
		id: 'rovo-platform-ui-components.inferred-project-card.inferredProjectCardMessages.reasonWrongContributors',
	},
	reasonAlreadyExists: {
		defaultMessage: 'Already exists',
		description: 'Feedback reason: the suggested project duplicates a project that already exists',
		id: 'rovo-platform-ui-components.inferred-project-card.inferredProjectCardMessages.reasonAlreadyExists',
	},
	reasonOther: {
		defaultMessage: 'Other',
		description: 'Feedback reason: some other problem with the suggested project',
		id: 'rovo-platform-ui-components.inferred-project-card.inferredProjectCardMessages.reasonOther',
	},
});

export default messages;
